import * as XLSX from "xlsx";

const PUBLIC_KEY = `
-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAojQVs6yFZR/Gs46x6wqc
8m1aeaSX3hXHvRDGKeEsJ7/Umb5GmPkUdlximFEPTyhCYSfR4WwtO4B3VaUStX35
JbUoeRwFFuz+z4ZR1Dr1CyKrnLthhcyG7WFxZ1nITXVI32ZTBZFskpcQ/JGO0y/d
9KbuVoLRU2r6IIoK3sfh4FhOkpmnyZA+jbuAU0ayUsjjHvbBcGja0Q3MOLlasxav
lmPWLrUkVV7Gp79p4edONXw81yG6b+WeJhjUqs8M3hxmFJpPA4GfOYze8q0kA++i
eEZIe30L4Te8GwkDYcNk1SBDScVHEcr+pwGoJB4DoCBODvSSzKn4G42z7ZKdmi1p
pQIDAQAB
-----END PUBLIC KEY-----
`;

function base64UrlEncode(data) {
  const bytes = data instanceof Uint8Array
    ? data
    : new Uint8Array(data);

  let binary = "";

  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(
      ...bytes.subarray(i, Math.min(i + 0x8000, bytes.length))
    );
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64Encode(data) {
  const bytes = data instanceof Uint8Array
    ? data
    : new Uint8Array(data);

  let binary = "";

  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(
      ...bytes.subarray(i, Math.min(i + 0x8000, bytes.length))
    );
  }

  return btoa(binary);
}

function pemToArrayBuffer(pem) {
  const base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s+/g, "");

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
}

async function getFileBytes(file) {
  const extension = file.name
    .split(".")
    .pop()
    .toLowerCase();

  // CSV
  if (extension === "csv") {
    return await file.arrayBuffer();
  }

  // Excel -> CSV -> bytes
  if (extension === "xlsx" || extension === "xls") {
    const buffer = await file.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array"
    });

    if (!workbook.SheetNames.length) {
      throw new Error("Excel file has no sheets.");
    }

    const worksheet =
      workbook.Sheets[workbook.SheetNames[0]];

    const csv = XLSX.utils.sheet_to_csv(worksheet);

    return new TextEncoder().encode(csv);
  }

  throw new Error(
    "Only CSV, XLSX and XLS files are supported."
  );
}

async function encryptJWE(payload, publicKeyPem) {

  // AES-256 CEK
  const cek = await crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256
    },
    true,
    ["encrypt"]
  );

  // RSA public key
  const publicKey = await crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(publicKeyPem),
    {
      name: "RSA-OAEP",
      hash: "SHA-256"
    },
    false,
    ["encrypt"]
  );

  // Encrypt AES key using RSA-OAEP-256
  const encryptedKey =
    await crypto.subtle.encrypt(
      {
        name: "RSA-OAEP"
      },
      publicKey,
      await crypto.subtle.exportKey(
        "raw",
        cek
      )
    );

  const header = {
    alg: "RSA-OAEP-256",
    enc: "A256GCM"
  };

  const encodedHeader =
    base64UrlEncode(
      new TextEncoder().encode(
        JSON.stringify(header)
      )
    );

  // 12-byte IV
  const iv = crypto.getRandomValues(
    new Uint8Array(12)
  );

  // AES-GCM encrypt
  const encryptedPayload =
    await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData:
          new TextEncoder().encode(encodedHeader)
      },
      cek,
      new TextEncoder().encode(payload)
    );

  const encryptedBytes =
    new Uint8Array(encryptedPayload);

  // GCM authentication tag = last 16 bytes
  const ciphertext =
    encryptedBytes.slice(
      0,
      encryptedBytes.length - 16
    );

  const tag =
    encryptedBytes.slice(
      encryptedBytes.length - 16
    );

  return [
    encodedHeader,
    base64UrlEncode(encryptedKey),
    base64UrlEncode(iv),
    base64UrlEncode(ciphertext),
    base64UrlEncode(tag)
  ].join(".");
}

/**
 * Convert CSV/XLS/XLSX -> CarePARVA .enc
 */
export async function convertToEnc(file) {

  if (!file) {
    throw new Error("Please select a file.");
  }

  // 1. Get original CSV bytes
  const fileBytes =
    await getFileBytes(file);

  if (!fileBytes.byteLength) {
    throw new Error("File is empty.");
  }

  // 2. IMPORTANT:
  // CarePARVA encrypts STANDARD BASE64
  // of the original file bytes.
  const base64Payload =
    base64Encode(fileBytes);

  // 3. Encrypt Base64 payload as JWE
  const jwe =
    await encryptJWE(
      base64Payload,
      PUBLIC_KEY
    );

  // 4. Create .enc file
  const blob = new Blob(
    [jwe],
    {
      type: "application/octet-stream"
    }
  );

  const outputName =
    file.name.replace(
      /\.(csv|xlsx|xls)$/i,
      ""
    ) + ".enc";

  return new File(
    [blob],
    outputName,
    {
      type: "application/octet-stream"
    }
  );
}
